import { useMemo } from 'react';

import { Box, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import ReactApexChart from 'react-apexcharts';
import { GroupButtonPeriod } from 'src/components/GroupButtonPeriod';
import { convertValue } from 'src/utils/number';

import { SummaryCards } from './SummaryCards';

import type {
  FicChildTradesHistoryItem,
  FicChildTradesHistoryPeriod,
  FicChildTradesHistoryResponse,
} from './types';
import type { ApexOptions } from 'apexcharts';

export interface FundMovementsChartProps {
  data: FicChildTradesHistoryResponse | null;
  period: FicChildTradesHistoryPeriod;
  onPeriodChange: (period: FicChildTradesHistoryPeriod) => void;
  onCompetenceSelect?: (competence: string) => void;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
}

const PERIOD_OPTIONS: Array<{
  buttonText: string;
  period: FicChildTradesHistoryPeriod;
}> = [
  { buttonText: '6 meses', period: '6m' },
  { buttonText: '12 meses', period: '12m' },
  { buttonText: 'Ótimo', period: 'all' },
];

function formatNetValue(value: number): string {
  if (value < 0) {
    return `- ${convertValue(Math.abs(value))}`;
  }
  return convertValue(value);
}

function formatMonthLabel(dateString: string): string {
  try {
    const formatted = format(parseISO(dateString), 'MMM/yy', { locale: ptBR });
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  } catch {
    return dateString;
  }
}

export function FundMovementsChart({
  data,
  period,
  onPeriodChange,
  onCompetenceSelect,
  isLoading = false,
  isError = false,
  errorMessage = 'Não foi possível carregar o histórico de movimentações.',
}: FundMovementsChartProps) {
  const theme = useTheme();

  const historyItems = useMemo<FicChildTradesHistoryItem[]>(
    () => data?.data ?? [],
    [data],
  );

  const totals = useMemo(
    () => data?.meta?.totals ?? { purchases: 0, sales: 0, net: 0 },
    [data],
  );

  const categories = useMemo(
    () => historyItems.map((item) => formatMonthLabel(item.competence)),
    [historyItems],
  );

  const series = useMemo(
    () => [
      {
        name: 'Compras',
        type: 'column',
        data: historyItems.map((item) => item.purchases),
      },
      {
        name: 'Vendas',
        type: 'column',
        data: historyItems.map((item) => -Math.abs(item.sales)),
      },
      {
        name: 'Valor líquido',
        type: 'line',
        data: historyItems.map((item) => item.net),
      },
    ],
    [historyItems],
  );

  const options = useMemo<ApexOptions>(() => {
    const purchasesColor = theme.palette.success.main;
    const salesColor = theme.palette.error.main;
    const netColor = theme.palette.info.main;

    return {
      chart: {
        type: 'line',
        height: 350,
        toolbar: { show: false },
        animations: { enabled: false },
        fontFamily: theme.typography.fontFamily,
        events: {
          dataPointSelection: (_event, _chartContext, config) => {
            if (config.seriesIndex > 1) return;

            const selectedItem = historyItems[config.dataPointIndex];
            if (selectedItem) {
              onCompetenceSelect?.(selectedItem.competence);
            }
          },
        },
      },
      colors: [purchasesColor, salesColor, netColor],
      stroke: {
        width: [0, 0, 3],
        curve: 'smooth',
      },
      plotOptions: {
        bar: {
          columnWidth: '40%',
          borderRadius: 2,
        },
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        type: 'category',
        categories,
        labels: {
          style: {
            colors: theme.palette.text.secondary,
            fontSize: '12px',
            fontWeight: 500,
          },
        },
        axisBorder: {
          color: theme.palette.divider,
        },
        axisTicks: {
          color: theme.palette.divider,
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: theme.palette.text.secondary,
            fontSize: '12px',
          },
          formatter: (val: number) =>
            convertValue(val, '', true, 'currency', 'BRL', 1, 0),
        },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'center',
        labels: {
          colors: theme.palette.text.primary,
        },
        markers: {
          radius: 12,
        },
      },
      grid: {
        borderColor: theme.palette.divider,
        strokeDashArray: 3,
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (val: number, { seriesIndex }) => {
            if (typeof val === 'undefined') return '';
            if (seriesIndex === 1) {
              return convertValue(Math.abs(val));
            }
            if (seriesIndex === 2) {
              return formatNetValue(val);
            }
            return convertValue(val);
          },
        },
      },
    };
  }, [theme, categories, historyItems, onCompetenceSelect]);

  const hasData = historyItems.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}>
        <GroupButtonPeriod
          periods={PERIOD_OPTIONS}
          selectedPeriod={period}
          handlePeriodChange={onPeriodChange}
          ariaLabel="Período do gráfico de movimentações"
          disabled={isLoading}
        />
      </Box>

      <SummaryCards totals={totals} />

      {isError ? (
        <Typography color="error" sx={{ py: 4, textAlign: 'center' }}>
          {errorMessage}
        </Typography>
      ) : isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 320,
          }}>
          <CircularProgress size={40} />
        </Box>
      ) : !hasData ? (
        <Typography
          color="text.secondary"
          sx={{ py: 6, textAlign: 'center', width: '100%' }}>
          Não há dados disponíveis no período selecionado.
        </Typography>
      ) : (
        <Box sx={{ width: '100%', minHeight: 350 }}>
          <ReactApexChart
            options={options}
            series={series}
            type="line"
            height={350}
          />
        </Box>
      )}
    </Box>
  );
}
